// Hexes (docs/04 §13): fine-print curses for HEX_DURATION spins (the spin they land on counts),
// at most HEX_MAX different ones. Salt removes one; 3+ Hex symbols in one paying pattern CLEANSE.
import { B } from '../content/balance';
import { HEX_LIST } from '../content/registry';
import { say } from './dialogue';
import { dims } from './grid';
import type { Modifiers } from './modifiers';
import type { Rng } from './rng';
import type { ActiveHex, GameEvent, GameState } from './types';

export function hexPool(mods: Modifiers) {
  return HEX_LIST.filter((h) => !h.requires || h.requires(mods));
}

export function applyRandomHex(state: GameState, mods: Modifiers, rng: Rng, by: string, events: GameEvent[]): void {
  const run = state.run!;
  if ((run.flags.hexImmune ?? 0) > 0) return;
  const pool = hexPool(mods).filter((h) => !run.hexes.some((a) => a.id === h.id));
  if (!pool.length) return;
  const def = rng.pick(pool);
  const hex: ActiveHex = { id: def.id, left: Math.max(1, mods.hexDuration) };
  if (def.targetsReel) hex.reel = rng.int(dims(run.size).reels);
  let replaced: string | undefined;
  if (run.hexes.length >= B.HEX_MAX) {
    // The one closest to expiring makes room.
    let idx = 0;
    run.hexes.forEach((h, i) => h.left < run.hexes[idx]!.left && (idx = i));
    replaced = run.hexes.splice(idx, 1)[0]!.id;
  }
  run.hexes.push(hex);
  if (mods.voodoo) run.flags.voodooSpin = run.spinIndex;
  events.push({ e: 'HEX_APPLIED', id: def.id, reel: hex.reel, replaced });
  say(state, 'hex', events);
}

/** Salt: the oldest hex goes. */
export function removeOneHex(state: GameState, by: string, events: GameEvent[]): void {
  const run = state.run!;
  const h = run.hexes.shift();
  if (h) events.push({ e: 'HEX_REMOVED', id: h.id, by });
}

export function cleanse(state: GameState, events: GameEvent[]): void {
  const run = state.run!;
  const removed = run.hexes.map((h) => h.id);
  run.hexes = [];
  events.push({ e: 'CLEANSE', removed });
  say(state, 'cleanse', events, undefined, { force: true });
}

/** At every banked spin: hexes count down and expire. */
export function tickHexes(state: GameState, events: GameEvent[]): void {
  const run = state.run!;
  for (const h of run.hexes) h.left--;
  for (const h of run.hexes.filter((x) => x.left <= 0)) events.push({ e: 'HEX_REMOVED', id: h.id, by: 'time' });
  run.hexes = run.hexes.filter((x) => x.left > 0);
}

export function clearHexes(state: GameState, by: string, events: GameEvent[]): void {
  const run = state.run!;
  for (const h of run.hexes) events.push({ e: 'HEX_REMOVED', id: h.id, by });
  run.hexes = [];
}
